import type {AuthHydroClickAttributes} from '../../marketing-header.types'
import {PersonIcon} from '@primer/octicons-react'
import {HeaderAppearanceSettings} from '../AppearanceSettings/HeaderAppearanceSettings'
import {dashboardAnalyticsEvent, signInAnalyticsEvent} from '../header-analytics'
import {AuthCTALink} from './AuthCTALink'
import styles from './AuthCTAs.module.css'

/** Dashboard CTA target, matching the legacy `href="/"`. */
const DASHBOARD_HREF = '/'

type MobileAuthActionsProps = {
  loggedIn: boolean
  marketingPage: boolean
  loginPath: string
  hydro: AuthHydroClickAttributes

  /** While the mobile menu is open, drop these CTAs from the trapped tab order (legacy parity). */
  isMenuOpen: boolean
  redesign?: boolean
}

/**
 * Mobile-only (`< lg`) top-row auth CTAs: a single Sign in (logged out) or Dashboard (logged in), plus
 * the appearance-settings trigger on logged-out, non-marketing pages. Desktop equivalents live in
 * {@link DesktopAuthActions} inside the expanding menu.
 */
export function MobileAuthActions({
  loggedIn,
  marketingPage,
  loginPath,
  hydro,
  isMenuOpen,
  redesign = false,
}: MobileAuthActionsProps) {
  const tabIndex = isMenuOpen ? -1 : undefined

  if (redesign) {
    return (
      <div className={styles.MobileAuthAction}>
        <AuthCTALink
          href={loggedIn ? DASHBOARD_HREF : loginPath}
          variant="subtle"
          analyticsEvent={loggedIn ? dashboardAnalyticsEvent : signInAnalyticsEvent}
          hydro={hydro}
        >
          <PersonIcon size={16} aria-hidden="true" />
          <span className="visually-hidden">{loggedIn ? 'Dashboard' : 'Sign in'}</span>
        </AuthCTALink>
      </div>
    )
  }

  return (
    <div className={styles.mobileActions}>
      {loggedIn ? (
        <AuthCTALink
          href={DASHBOARD_HREF}
          variant="subtle"
          analyticsEvent={dashboardAnalyticsEvent}
          hydro={hydro}
          tabIndex={tabIndex}
        >
          Dashboard
        </AuthCTALink>
      ) : (
        <>
          <AuthCTALink
            href={loginPath}
            variant="subtle"
            analyticsEvent={signInAnalyticsEvent}
            hydro={hydro}
            tabIndex={tabIndex}
          >
            Sign in
          </AuthCTALink>

          {!marketingPage ? <HeaderAppearanceSettings /> : null}
        </>
      )}
    </div>
  )
}
